import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, Database, UserCheck, FileText } from 'lucide-react';

const Privacy: React.FC = () => {
  const sections = [
    {
      icon: Database,
      title: 'Information We Collect',
      content: [
        'Account information (username, email, password)',
        'Typing performance data (WPM, accuracy, test results)',
        'Usage statistics and preferences',
        'Device and browser information for optimization',
        'IP address for security purposes'
      ]
    },
    {
      icon: Lock,
      title: 'How We Use Your Information',
      content: [
        'Provide and improve our typing test services',
        'Track your progress and display personalized statistics',
        'Enable multiplayer battles and leaderboards',
        'Send important service updates and notifications',
        'Prevent fraud and ensure platform security',
        'Analyze usage patterns to improve user experience'
      ]
    },
    {
      icon: Shield,
      title: 'Data Security',
      content: [
        'All passwords are encrypted using industry-standard hashing',
        'Secure HTTPS connections for all data transmission',
        'Regular security audits and updates',
        'Limited employee access to user data',
        'Secure database infrastructure with encryption at rest',
        'Automatic logout after periods of inactivity'
      ]
    },
    {
      icon: Eye,
      title: 'Data Sharing',
      content: [
        'We do not sell your personal information to third parties',
        'Public leaderboards display only usernames and scores',
        'Anonymous usage analytics help improve the platform',
        'Legal compliance: We may disclose information if required by law',
        'Service providers who help operate our platform (under strict agreements)'
      ]
    },
    {
      icon: UserCheck,
      title: 'Your Rights',
      content: [
        'Access your personal data at any time through your profile',
        'Request deletion of your account and associated data',
        'Export your typing statistics and history',
        'Opt-out of non-essential communications',
        'Update your information and preferences',
        'Request clarification about how we use your data'
      ]
    },
    {
      icon: FileText,
      title: 'Cookies and Tracking',
      content: [
        'Essential cookies for login and session management',
        'Performance cookies to analyze site usage',
        'Preference cookies to remember your settings',
        'You can control cookies through your browser settings',
        'Some features may not work properly if cookies are disabled'
      ]
    }
  ];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-12"
      >
        <h1 className="text-5xl md:text-6xl font-bold mb-6">
          Privacy <span className="text-accent">Policy</span>
        </h1>
        <p className="text-textMuted text-lg">
          Last updated: January 24, 2026
        </p>
        <p className="text-textMuted mt-4 max-w-2xl mx-auto">
          At TypeRush, we take your privacy seriously. This policy explains how we collect, 
          use, and protect your personal information.
        </p>
      </motion.div>

      {/* Quick Summary */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="bg-accent/10 border border-accent/20 rounded-xl p-6 mb-12"
      >
        <h2 className="text-2xl font-bold mb-4 text-accent flex items-center">
          <Shield className="w-6 h-6 mr-2" />
          Quick Summary
        </h2>
        <ul className="space-y-2 text-textMuted">
          <li className="flex items-start">
            <span className="text-accent mr-2">•</span>{' '}
            We collect only the data necessary to provide our services
          </li>
          <li className="flex items-start">
            <span className="text-accent mr-2">•</span>{' '}
            Your personal information is never sold to third parties
          </li>
          <li className="flex items-start">
            <span className="text-accent mr-2">•</span>{' '}
            We use industry-standard security measures to protect your data
          </li>
          <li className="flex items-start">
            <span className="text-accent mr-2">•</span>{' '}
            You have full control over your data and can delete your account anytime
          </li>
        </ul>
      </motion.div>

      {/* Detailed Sections */}
      <div className="space-y-8 mb-12">
        {sections.map((section, index) => {
          const Icon = section.icon;
          return (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
              className="bg-cardBg rounded-xl p-8 border border-secondary"
            >
              <h2 className="text-2xl font-bold mb-4 flex items-center">
                <Icon className="w-6 h-6 mr-3 text-accent" />
                {section.title}
              </h2>
              <ul className="space-y-3">
                {section.content.map((item) => (
                  <li key={item} className="flex items-start text-textMuted">
                    <span className="text-accent mr-3 mt-1">→</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          );
        })}
      </div>

      {/* Children's Privacy */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1 }}
        className="bg-cardBg rounded-xl p-8 border border-secondary mb-12"
      >
        <h2 className="text-2xl font-bold mb-4">Children's Privacy</h2>
        <p className="text-textMuted mb-4">
          TypeRush is designed to be accessible to users of all ages. However, we require parental 
          consent for users under 13 years old. If we learn that we have collected personal information 
          from a child under 13 without proper consent, we will delete that information immediately.
        </p>
        <p className="text-textMuted">
          Parents who believe their child has provided us with personal information may contact us 
          to request deletion of that information.
        </p>
      </motion.div>

      {/* Changes to Policy */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.2 }}
        className="bg-cardBg rounded-xl p-8 border border-secondary mb-12"
      >
        <h2 className="text-2xl font-bold mb-4">Changes to This Policy</h2>
        <p className="text-textMuted mb-4">
          We may update this privacy policy from time to time to reflect changes in our practices 
          or for legal, operational, or regulatory reasons. We will notify users of any material 
          changes by:
        </p>
        <ul className="space-y-2 text-textMuted mb-4">
          <li className="flex items-start">
            <span className="text-accent mr-2">•</span>{' '}
            Posting the updated policy on our website with a new "Last Updated" date
          </li>
          <li className="flex items-start">
            <span className="text-accent mr-2">•</span>{' '}
            Sending an email notification to registered users
          </li>
          <li className="flex items-start">
            <span className="text-accent mr-2">•</span>{' '}
            Displaying a prominent notice on the platform
          </li>
        </ul>
        <p className="text-textMuted">
          Your continued use of TypeRush after any changes indicates your acceptance of the 
          updated privacy policy.
        </p>
      </motion.div>

      {/* Contact */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.4 }}
        className="bg-accent/10 border border-accent/20 rounded-xl p-8 text-center"
      >
        <h2 className="text-2xl font-bold mb-4">Questions About Privacy?</h2>
        <p className="text-textMuted mb-6">
          If you have any questions or concerns about our privacy practices, please don't 
          hesitate to contact us.
        </p>
        <div className="text-textMuted">
          <p className="mb-2">Email: <span className="text-accent">privacy@typerush.com</span></p>
          <p>Response time: Within 48 hours</p>
        </div>
      </motion.div>
    </div>
  );
};

export default Privacy;
